import React, { useContext, useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../../services/API'
import { GlobalContext, UserContext } from '../../../../App'
import { AssetContext } from './SingleAsset'

// Shelves the asset in a kit store (Make Ready Stores). The server clears
// `vehicle` and `parentAsset` at the same time - in a store means not on a
// vehicle and not inside another asset.
const AssignToStoreDialog = ({ open, onClose }) => {
  const { asset, setAsset } = useContext(AssetContext)
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const [storeList, setStoreList] = useState([])
  const [loadingStores, setLoadingStores] = useState(false)
  const [selectedStore, setSelectedStore] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setSelectedStore(null)
    setLoadingStores(true)
    API.get('/kitstores')
      .then((res) => res.status === 200 && setStoreList(res.data ?? []))
      .catch(() => setStoreList([]))
      .finally(() => setLoadingStores(false))
  }, [open])

  const handleAssign = () => {
    if (!selectedStore || saving) return
    setSaving(true)
    const comment = `Asset moved to ${selectedStore.name}`
    API.put('/asset/' + asset._id, {
      store: selectedStore._id,
      $push: { comments: { comment, author: user._id } },
    })
      .then((res) => {
        if (res.status === 200) {
          setAsset((prev) => ({
            ...prev,
            store: selectedStore,
            vehicle: null,
            parentAsset: null,
            comments: [
              ...(prev?.comments ?? []),
              {
                comment,
                author: {
                  _id: user._id,
                  firstName: user.firstName,
                  lastName: user.lastName,
                  photograph: user.photograph,
                },
                createdAt: new Date(),
                _id: new Date().toISOString(),
              },
            ],
          }))
          setToast({ message: `Moved to ${selectedStore.name}` })
          onClose()
        } else {
          setToast({
            message: 'Unable to update asset. Please try again later.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to update asset. Please try again later.',
          severity: 'error',
        }),
      )
      .finally(() => setSaving(false))
  }

  const currentPlace = asset.vehicle?.callSign
    ? `on ${asset.vehicle.callSign}`
    : asset.parentAsset?.assetID
      ? `inside ${asset.parentAsset.assetID}`
      : asset.store?.name
        ? `in ${asset.store.name}`
        : null

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 800 }}>
        Move {asset.assetID} to a store
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {currentPlace
            ? `This asset is currently ${currentPlace}. Moving it to a store takes it out of there.`
            : 'The asset will show as shelved in the selected kit store.'}
          {!loadingStores && storeList.length === 0 && (
            <> No kit stores exist yet — create one on the Make Ready board&apos;s Stores tab.</>
          )}
        </DialogContentText>
        <Autocomplete
          options={storeList.filter((s) => s._id !== asset.store?._id)}
          loading={loadingStores}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          value={selectedStore}
          onChange={(e, value) => setSelectedStore(value)}
          getOptionLabel={(store) => store.name ?? ''}
          renderOption={(props, store) => (
            <Box component="li" {...props} key={store._id}>
              <Stack>
                <Typography fontWeight={700}>{store.name}</Typography>
                {store.baseLocation?.name && (
                  <Typography variant="caption" color="text.secondary">
                    {store.baseLocation.name}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Kit store" autoFocus />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selectedStore || saving}
          onClick={handleAssign}
        >
          {saving ? 'Moving…' : 'Move to store'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AssignToStoreDialog
